import dynamic from 'next/dynamic';
import { ComponentProps, useCallback, useState } from 'react';
import type CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import type UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Calendar from '@alltrails/denali/icons/Calendar';
import { useExperiments } from '@alltrails/experiments';
import type { AuthedEvent, EventMapType } from '../../types';

const CreateEventModal = dynamic(() => import('../CreateEventModal'), { ssr: false });
const ShareEventModal = dynamic(() => import('../ShareEventModal'), { ssr: false });
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

type CreateEventButtonProps = Pick<ComponentProps<typeof IconButton>, 'size' | 'variant'> & {
  mapId: number;
  mapType: EventMapType;
};

const CreateEventButton = ({ mapId, mapType, ...iconButtonProps }: CreateEventButtonProps) => {
  const user = useUser();
  const isMobile = useIsMobileSizedScreen();
  const languageRegionCode = useLanguageRegionCode();

  const [createdEvent, setCreatedEvent] = useState<AuthedEvent>();
  const [isCreateEventModalOpen, setIsCreateEventModalOpen] = useState(false);
  const [isShareEventModalOpen, setIsShareEventModalOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);

  const onClick = useCallback(() => {
    if (user) {
      setIsCreateEventModalOpen(true);
    } else {
      setIsSignupModalOpen(true);
    }
  }, [user]);

  const onEventCreation = useCallback(
    (event: AuthedEvent) => {
      setIsCreateEventModalOpen(false);
      const eventSlug = wrapUrlSafe(`/event/${event.id}`, languageRegionCode);
      if (isMobile) {
        try {
          navigator.share({ title: event.title, text: event.title, url: window.location.origin + eventSlug });
        } catch {
          setCreatedEvent(event);
          setIsShareEventModalOpen(true);
        }
      } else {
        window.location.assign(eventSlug);
      }
    },
    [isMobile, languageRegionCode]
  );

  return (
    <>
      {/* TODO: EVENT TRANSLATIONS */}
      <IconButton {...iconButtonProps} icon={{ Component: Calendar }} onClick={onClick} title="Create invite" />
      {isSignupModalOpen && (
        <SignUpModal
          isOpen={isSignupModalOpen}
          closeModal={() => setIsSignupModalOpen(false)}
          onSuccess={() => {
            setIsSignupModalOpen(false);
            setIsCreateEventModalOpen(true);
          }}
          trigger={'' as CarouselDisplayTrigger}
          upsellTrigger={'' as UpsellTrigger}
        />
      )}
      {isCreateEventModalOpen && (
        <CreateEventModal
          mapId={mapId}
          mapType={mapType}
          onCloseRequest={() => setIsCreateEventModalOpen(false)}
          onSuccess={onEventCreation}
          // Override the submit button text for mobile users since the share flow is triggered instead
          submitButtonText={isMobile ? 'Invite friends' : undefined}
        />
      )}
      {isShareEventModalOpen && createdEvent && (
        <ShareEventModal
          isOpen
          onClose={() => {
            setIsShareEventModalOpen(false);
            setCreatedEvent(undefined);
          }}
          event={createdEvent}
        />
      )}
    </>
  );
};

const CreateEventButtonWrapper = (props: CreateEventButtonProps) => {
  const experiments = useExperiments();

  if (experiments['web-disco-events']?.value === 'on') {
    return <CreateEventButton {...props} />;
  }

  return null;
};

export default CreateEventButtonWrapper;
